/* iter120 — Bucket C Field Schedule cleanup.
   ONE consistent mobile-first field experience for every internal user
   (technician, service manager, super admin). Permissions change the action
   buttons inside a job, not the entire layout.

   Layout:
   ┌────────────────────────────────────────────────────────────┐
   │  Field Schedule                            ⟳ refresh        │
   │  ◀  Monday, March 3   ▶   [Today]                           │
   │  ┌──────────────┐ ┌──────────────┐                          │
   │  │  My Day      │ │  Team Day    │  ← segmented toggle      │
   │  └──────────────┘ └──────────────┘                          │
   │                                                              │
   │  My Day:                                                     │
   │    My Jobs Today (n)                                         │
   │      [vertical card] tap → preview sheet (Claim Shift /      │
   │                              Complete Job / Status…)         │
   │    Available to Claim Today (n)                              │
   │      [vertical card] tap → preview sheet (Claim Job)         │
   │                                                              │
   │  Team Day:                                                   │
   │    Dan (n)    [vertical card]                                │
   │    Adam (n)   [vertical card]                                │
   │    Austin (n) [vertical card]                                │
   │    Unassigned (n)  ← claimable jobs always live here         │
   └────────────────────────────────────────────────────────────┘

   Dispatcher weekly grid stays at /admin/schedule — that page is unchanged.
*/
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import {
  ChevronLeft, ChevronRight, Clock, Briefcase, Lock,
  Hand, Calendar as CalendarIcon, Users as UsersIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import CompletionWizard from '../../components/CompletionWizard';
import ClaimShiftDialog from '../../components/communication/ClaimShiftDialog';
import OnCallBanner from '../../components/OnCallBanner';
import RefreshIndicator from '../../components/RefreshIndicator';
import JobPreviewSheet from '../../components/JobPreviewSheet';
import { AddressLink } from '../../components/MapsNavProvider';
import ScheduleSearchDialog from '../../components/schedule/ScheduleSearchDialog';
import UnfinishedBillingBanner from '../../components/UnfinishedBillingBanner';
import VehicleCheckCard from '../../components/VehicleCheckCard';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { todayISO_ET } from '../../utils/tz';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter120 — Pure ISO-string day arithmetic. Going through `new Date('Y-M-DT00:00:00')`
// was browser-local; combined with the ET-anchored helpers below it produced
// off-by-one bugs in non-ET browsers (the day-nav buttons did nothing in UTC).
// This keeps the math entirely in YYYY-MM-DD space.
const addDaysISO = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  // Use UTC math so DST / browser-TZ never affects the result.
  const t = Date.UTC(y, m - 1, d) + n * 86400000;
  const x = new Date(t);
  const yy = x.getUTCFullYear();
  const mm = String(x.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(x.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};
const fmtWeekdayLong = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
};
const fmtMonthDay = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const fmtTime12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
};
const computeHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return Math.max(0, (eh + em / 60) - (sh + sm / 60));
};

// Stable color per tech across days/jobs.
const TECH_HUES = ['#D4AF37', '#60A5FA', '#34D399', '#F87171', '#A78BFA', '#FB923C', '#F472B6', '#94A3B8'];
const techColor = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return TECH_HUES[h % TECH_HUES.length];
};

const STATUS_PILL = {
  scheduled:        'text-[#D4AF37] bg-[#D4AF37]/10',
  in_progress:      'text-blue-300  bg-blue-500/15',
  en_route:         'text-blue-200  bg-blue-500/20',
  pending_approval: 'text-orange-300 bg-orange-500/15',
  completed:        'text-green-400 bg-green-500/15',
  cancelled:        'text-red-300   bg-red-500/15',
};

const ADMIN_TIER = new Set(['super_admin', 'admin', 'manager', 'service_manager', 'construction_manager', 'office_staff']);

// iter120 — JobCard is hoisted out of EmployeeSchedule so React doesn't
// remount every card on each parent render (no-unstable-nested-components).
const JobCard = ({ job, kind, currentUser, onOpen, onClaim }) => {
  const myHelper = (job.helpers || []).find((h) => h.employee_id === currentUser?._id);
  const isPrimary = job.employee_id === currentUser?._id;
  const isHelper = !!myHelper && !isPrimary;
  return (
    <button
      data-testid={`field-card-${kind}-${job.id}`}
      onClick={() => onOpen(job, kind)}
      style={{ borderLeft: `4px solid ${techColor(job.employee_id || 'unassigned')}` }}
      className="w-full text-left bg-[#1A1A1A] hover:bg-[#262626] active:bg-[#2a2a2a] rounded-md p-3 transition-colors border border-[#262626]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[#D4AF37] text-xs font-bold">
              {job.all_day ? 'All day' : `${fmtTime12(job.start_time)} – ${fmtTime12(job.end_time)}`}
            </span>
            {kind === 'claim' && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-300 text-[10px] font-bold uppercase tracking-wider">
                <Hand className="w-2.5 h-2.5" /> Claimable
              </span>
            )}
            {isHelper && (
              <span data-testid={`field-helper-badge-${job.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 text-[10px] font-bold uppercase tracking-wider">
                You · Helper
              </span>
            )}
            {isPrimary && kind !== 'claim' && (
              <span data-testid={`field-primary-badge-${job.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider">
                You · Primary
              </span>
            )}
            {job.status && job.status !== 'scheduled' && (
              <span data-testid={`field-status-badge-${job.id}`} className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${job.visit_outcome === 'return_visit' ? 'text-sky-200 bg-sky-500/15' : (STATUS_PILL[job.status] || 'text-[#A3A3A3] bg-[#262626]')}`}>
                {job.visit_outcome === 'return_visit' ? 'Visit done — return needed' : job.status.replace('_', ' ')}
              </span>
            )}
            {/* iter169 — Partial closeout flag pulled from /schedule. Stays visible
                even after job.status flips to "completed" so it can't be missed. */}
            {job.needs_invoice_decision && (
              <span data-testid={`field-finish-billing-badge-${job.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-500/50 text-red-200 text-[10px] font-bold uppercase tracking-wider">
                Finish Billing
              </span>
            )}
          </div>
          <p className="text-white text-sm font-medium mt-1 line-clamp-2">{job.shift_title || job.service_type || 'Job'}</p>
          {job.customer_name && (
            <p className="text-[#A3A3A3] text-xs mt-0.5 truncate">{job.customer_name}</p>
          )}
          {(job.pm_company_id || job.pm_company_name) && (
            <p data-testid={`field-card-requester-${job.id}`} className="text-cyan-300 text-xs mt-0.5 truncate">
              Requested by: {job.requester_state === 'unknown' ? 'Unknown' : (job.pm_requester_name || '—')}
            </p>
          )}
          {job.address && (
            <AddressLink
              address={job.address}
              testId={`field-card-address-${job.id}`}
              className="text-[#737373] text-xs mt-0.5 truncate max-w-full"
              iconClassName="w-3 h-3 shrink-0"
            />
          )}
        </div>
        {kind === 'claim' && (
          <Button
            data-testid={`field-card-claim-btn-${job.id}`}
            onClick={(e) => { e.stopPropagation(); onClaim(job); }}
            size="sm"
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold shrink-0"
          >
            <Hand className="w-3.5 h-3.5 mr-1" /> Claim
          </Button>
        )}
      </div>
    </button>
  );
};

const EmployeeSchedule = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // Mode persisted into URL (?view=my|team) so refresh / share-link works.
  const initialMode = (params.get('view') === 'team') ? 'team' : 'my';
  const [mode, setMode] = useState(initialMode);
  const [date, setDate] = useState(todayISO_ET());

  // My Day data
  const [myAppointments, setMyAppointments] = useState([]);   // jobs assigned to me (primary or helper) on `date`
  const [claimable, setClaimable] = useState([]);             // claimable jobs ON THE SELECTED DAY only
  // Team Day data
  const [teamJobs, setTeamJobs] = useState([]);               // /employee/team-schedule rows on `date`

  // Detail sheet + flows
  const [selected, setSelected] = useState(null);
  const [origin, setOrigin] = useState('my');                 // 'my' | 'team' | 'claim'
  const [showWizard, setShowWizard] = useState(false);
  const [wizardOutcome, setWizardOutcome] = useState('');
  const [claimOpen, setClaimOpen] = useState(false);
  const [latestCompletion, setLatestCompletion] = useState(null);
  const [lockStatus, setLockStatus] = useState({ locked: false });

  // iter316 — Keep an open closeout across app-switching / remounts / reloads. The open
  // Job+Visit pointer is per-user; the wizard restores its own fields from its scoped draft.
  const OPEN_KEY = user?._id ? `nexflow_field_open_closeout__${user._id}` : null;
  useEffect(() => {
    if (!OPEN_KEY) return;
    try {
      const d = JSON.parse(localStorage.getItem(OPEN_KEY) || 'null');
      if (d?.appt?.id && Date.now() - (d.at || 0) < 24 * 3600 * 1000) {
        setSelected(d.appt);
        setWizardOutcome(d.outcome || '');
        setShowWizard(true);
      }
    } catch { /* ignore corrupt pointer */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [OPEN_KEY]);
  useEffect(() => {
    if (!OPEN_KEY) return;
    try {
      if (showWizard && selected?.id) localStorage.setItem(OPEN_KEY, JSON.stringify({ appt: selected, outcome: wizardOutcome, at: Date.now() }));
      else if (!showWizard) localStorage.removeItem(OPEN_KEY);
    } catch { /* storage unavailable */ }
  }, [OPEN_KEY, showWizard, selected, wizardOutcome]);

  const isAdminTier = ADMIN_TIER.has(user?.role);

  // Sync URL when mode changes — but NOT on first render.
  useEffect(() => {
    const cur = params.get('view') || 'my';
    if (cur !== mode) {
      const np = new URLSearchParams(params);
      np.set('view', mode);
      setParams(np, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ── Data fetches ──
  const fetchMyDay = useCallback(async () => {
    if (!user?._id) return;
    try {
      const { data } = await axios.get(`${API}/schedule`, {
        withCredentials: true,
        params: {
          start_date: date, end_date: date,
          ...(isAdminTier ? {} : { employee_id: user._id }),
        },
      });
      // For non-admin: backend already scopes. For admin: filter to MY primary+helper jobs.
      const mine = (data || []).filter((a) => {
        if (a.employee_id === user._id) return true;
        if ((a.helpers || []).some((h) => h.employee_id === user._id)) return true;
        return false;
      });
      setMyAppointments(mine);
    } catch (err) { console.error(err); }
  }, [user?._id, date, isAdminTier]);

  const fetchClaimableForDay = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employee/claimable-jobs`, { withCredentials: true });
      // Backend returns ALL future-claimable; we slice down to the selected date.
      setClaimable((Array.isArray(data) ? data : []).filter((j) => j.date === date));
    } catch (err) {
      if (err.response?.status !== 403) console.error('claimable load failed', err);
      setClaimable([]);
    }
  }, [date]);

  const fetchTeamDay = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employee/team-schedule`, {
        withCredentials: true,
        params: { start_date: date, end_date: date },
      });
      setTeamJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('team-schedule load failed', err);
      setTeamJobs([]);
    }
  }, [date]);

  const fetchLock = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/employee/next-job-lock`, { withCredentials: true });
      setLockStatus(data);
    } catch { /* ignore */ }
  }, []);

  const fetchLatestCompletion = useCallback(async () => {
    if (!selected || origin !== 'my') { setLatestCompletion(null); return; }
    try {
      const { data } = await axios.get(`${API}/jobs/completion/by-appointment/${selected.id}`, { withCredentials: true });
      setLatestCompletion(data);
    } catch { setLatestCompletion(null); }
  }, [selected, origin]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      mode === 'my' ? fetchMyDay() : Promise.resolve(),
      mode === 'my' ? fetchClaimableForDay() : Promise.resolve(),
      mode === 'team' ? fetchTeamDay() : Promise.resolve(),
      fetchLock(),
      fetchLatestCompletion(),
    ]);
  }, [mode, fetchMyDay, fetchClaimableForDay, fetchTeamDay, fetchLock, fetchLatestCompletion]);

  const auto = useAutoRefresh(refreshAll, { pausedByWizard: showWizard || claimOpen });

  useEffect(() => {
    if (mode === 'my') { fetchMyDay(); fetchClaimableForDay(); }
    else               { fetchTeamDay(); }
    fetchLock();
  }, [mode, date, fetchMyDay, fetchClaimableForDay, fetchTeamDay, fetchLock]);

  useEffect(() => { fetchLatestCompletion(); }, [fetchLatestCompletion]);

  // ── Actions ──
  const handleClaimJob = async (job) => {
    if (!window.confirm(`Claim this job?\n\n${job.service_type || job.shift_title}\n${fmtTime12(job.start_time)}–${fmtTime12(job.end_time)}\n${job.customer_name || ''}`)) return;
    try {
      await axios.post(`${API}/schedule/${job.id}/claim-job`, {}, { withCredentials: true });
      toast.success('Job claimed — added to your schedule');
      setSelected(null);
      // Optimistically remove from claimable + team-unassigned bucket then reload.
      setClaimable((prev) => prev.filter((j) => j.id !== job.id));
      setTeamJobs((prev) => prev.map((j) => j.id === job.id ? { ...j, employee_id: user._id, employee_name: user.name } : j));
      refreshAll();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        toast.error(detail || 'This job was just claimed by someone else.');
        refreshAll();
      } else if (status === 403) {
        toast.error(detail || 'You are not eligible to claim this job.');
      } else {
        toast.error(detail || 'Failed to claim job.');
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/schedule/${id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      setSelected((s) => s ? { ...s, status: newStatus } : s);
      refreshAll();
    } catch { toast.error('Failed to update status'); }
  };

  const openWizard = () => {
    if (!selected) return;
    setWizardOutcome('');
    const isAssigned = selected.employee_id === user?._id;
    const isUnclaimed = ['draft', 'scheduled'].includes(selected.status) && !selected.claimed_at;
    if (isUnclaimed) {
      const msg = isAdminTier && !isAssigned
        ? `This job has not been claimed yet.\n\nContinue as Admin Override — completion will be credited to the assigned tech (${selected.employee_name || 'unassigned'}), and your override will be logged.`
        : `This shift has not been claimed yet.\n\nClaim it now and continue to the completion wizard?`;
      if (!window.confirm(msg)) return;
    }
    setShowWizard(true);
  };

  // Create Additional Estimate — reuse the canonical CompletionWizard but jump it
  // straight into the estimate-builder path (initialOutcome='estimate_only').
  const openEstimateWizard = () => {
    if (!selected) return;
    setWizardOutcome('estimate_only');
    setShowWizard(true);
  };

  // ── Card open helper (preview sheet) ──
  const openPreview = (job, originType) => {
    setOrigin(originType);
    setSelected(job);
  };

  // iter143 — Start Job / Arrived stamps actual_start_time. Optimistically updates
  // the selected card so the sheet flips to "ON JOB since hh:mm" without a refresh.
  const handleStartJob = async () => {
    if (!selected) return;
    try {
      const { data } = await axios.post(`${API}/schedule/${selected.id}/start-job`, {}, { withCredentials: true });
      if (data?.actual_start_time) {
        setSelected({ ...selected, actual_start_time: data.actual_start_time, status: 'in_progress' });
      }
      toast.success('On job — start time recorded');
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start job');
    }
  };

  // ── Team grouping (memoized) ──
  const teamGroups = useMemo(() => {
    const groups = new Map();
    for (const j of teamJobs) {
      const key = j.employee_id || 'unassigned';
      if (!groups.has(key)) {
        groups.set(key, {
          id: key,
          name: key === 'unassigned' ? 'Unassigned' : (j.employee_name || 'Tech'),
          color: key === 'unassigned' ? '#F87171' : techColor(key),
          jobs: [],
        });
      }
      groups.get(key).jobs.push(j);
    }
    // Sort each group by start_time
    for (const g of groups.values()) {
      g.jobs.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    }
    const ordered = Array.from(groups.values());
    // Unassigned to the top so claimable jobs are highly visible.
    ordered.sort((a, b) => {
      if (a.id === 'unassigned') return -1;
      if (b.id === 'unassigned') return 1;
      return a.name.localeCompare(b.name);
    });
    return ordered;
  }, [teamJobs]);

  // Quick stats for the day
  const myHours = myAppointments.reduce((acc, a) => acc + computeHours(a.start_time, a.end_time), 0);
  const isToday = date === todayISO_ET();
  const dateLabel = `${fmtWeekdayLong(date)}, ${fmtMonthDay(date)}`;

  return (
    <div data-testid="field-schedule-page" className="space-y-4">
      {lockStatus.locked && (
        <div data-testid="next-job-lock-banner" className="bg-[#D4AF37]/10 border border-[#D4AF37] rounded-md p-3 flex items-center gap-3">
          <Lock className="w-5 h-5 text-[#D4AF37] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium">Finish your in-progress job first</p>
            <p className="text-[#A3A3A3] text-xs">{lockStatus.message} {lockStatus.blocking_title && <span className="text-white">• {lockStatus.blocking_title}</span>}</p>
          </div>
          {lockStatus.blocking_appointment_id && (
            <Button
              data-testid="finish-closeout-btn"
              size="sm"
              onClick={() => {
                const appt = myAppointments.find((a) => a.id === lockStatus.blocking_appointment_id);
                if (appt) { setOrigin('my'); setSelected(appt); setShowWizard(true); }
                else { toast.error("Couldn't find that job today — switch days to find it."); }
              }}
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] shrink-0"
            >
              Finish Closeout
            </Button>
          )}
        </div>
      )}

      <OnCallBanner />

      {/* iter179 — Unfinished billing banner (today + prior-day) */}
      <UnfinishedBillingBanner />

      {/* Weekly Monday vehicle check (assigned truck) */}
      <VehicleCheckCard />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-white">Field Schedule</h2>
          <p className="text-[#A3A3A3] text-sm">One day, one place. Tap a card for full details.</p>
        </div>
        <div className="flex items-center gap-2">
          <ScheduleSearchDialog onOpenResult={(row) => openPreview(row, 'team')} />
          <RefreshIndicator
            refresh={auto.refresh}
            lastUpdated={auto.lastUpdated}
            isFetching={auto.isFetching}
            isPaused={auto.isPaused}
            testId="field-sched-refresh"
          />
        </div>
      </div>

      {/* Day Nav */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-[#141414] border border-[#262626] rounded-md p-1">
          <Button
            data-testid="field-prev-day"
            onClick={() => setDate(addDaysISO(date, -1))}
            variant="ghost" size="sm" className="text-white hover:bg-[#262626] h-9 px-2"
          ><ChevronLeft className="w-4 h-4" /></Button>
          <div className="px-3 text-center min-w-[180px]">
            <p data-testid="field-day-label" className="text-white text-sm font-medium">{dateLabel}</p>
            {isToday && <p className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-bold">Today</p>}
          </div>
          <Button
            data-testid="field-next-day"
            onClick={() => setDate(addDaysISO(date, 1))}
            variant="ghost" size="sm" className="text-white hover:bg-[#262626] h-9 px-2"
          ><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <Button
          data-testid="field-today-btn"
          onClick={() => setDate(todayISO_ET())}
          variant="outline" size="sm" disabled={isToday}
          className="border-[#262626] text-white hover:bg-[#262626] disabled:opacity-50"
        >
          <CalendarIcon className="w-4 h-4 mr-1" /> Today
        </Button>
      </div>

      {/* Segmented toggle */}
      <div data-testid="field-mode-toggle" className="grid grid-cols-2 gap-0 bg-[#141414] border border-[#262626] rounded-md p-1 max-w-md mx-auto">
        <button
          data-testid="field-toggle-my-day"
          onClick={() => setMode('my')}
          className={`min-h-[44px] rounded-md text-sm font-bold uppercase tracking-wider transition-colors ${
            mode === 'my' ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'text-white hover:bg-[#1f1f1f]'
          }`}
        >
          My Day
        </button>
        <button
          data-testid="field-toggle-team-day"
          onClick={() => setMode('team')}
          className={`min-h-[44px] rounded-md text-sm font-bold uppercase tracking-wider transition-colors ${
            mode === 'team' ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'text-white hover:bg-[#1f1f1f]'
          }`}
        >
          Team Day
        </button>
      </div>

      {/* ─────────────────────── MY DAY ─────────────────────── */}
      {mode === 'my' && (
        <div data-testid="field-my-day" className="space-y-6">
          {/* Section: My Jobs */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">My Jobs Today</h3>
                <p className="text-[#A3A3A3] text-[11px]">Where you&rsquo;re primary or helper</p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-[#A3A3A3] flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {myAppointments.length}</span>
                <span className="text-[#A3A3A3] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {myHours.toFixed(1)}h</span>
              </div>
            </div>
            {myAppointments.length === 0 ? (
              <div data-testid="field-my-empty" className="bg-[#141414] border border-[#262626] rounded-md p-6 text-center">
                <p className="text-[#A3A3A3] text-sm">No jobs assigned to you {isToday ? 'today' : 'on this day'}.</p>
                {claimable.length > 0 && (
                  <p className="text-[#737373] text-xs mt-1">
                    {claimable.length} claimable job{claimable.length === 1 ? '' : 's'} available below.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {myAppointments
                  .slice()
                  .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                  .map((j) => <JobCard key={j.id} job={j} kind="my" currentUser={user} onOpen={openPreview} onClaim={handleClaimJob} />)
                }
              </div>
            )}
          </section>

          {/* Section: Available to Claim */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Available to Claim Today</h3>
                <p className="text-[#A3A3A3] text-[11px]">First-come, first-served. Tap a card to preview before claiming.</p>
              </div>
              <span className="text-[#A3A3A3] text-xs flex items-center gap-1">
                <Hand className="w-3.5 h-3.5" /> {claimable.length}
              </span>
            </div>
            {claimable.length === 0 ? (
              <div data-testid="field-claim-empty" className="bg-[#141414] border border-[#262626] rounded-md p-6 text-center">
                <p className="text-[#A3A3A3] text-sm">No claimable jobs {isToday ? 'today' : 'on this day'}.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {claimable.map((j) => <JobCard key={j.id} job={j} kind="claim" currentUser={user} onOpen={openPreview} onClaim={handleClaimJob} />)}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ─────────────────────── TEAM DAY ─────────────────────── */}
      {mode === 'team' && (
        <div data-testid="field-team-day" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">Team Day</h3>
              <p className="text-[#A3A3A3] text-[11px]">Everyone&rsquo;s day, grouped by tech. Unassigned jobs at the top.</p>
            </div>
            <span className="text-[#A3A3A3] text-xs flex items-center gap-1">
              <UsersIcon className="w-3.5 h-3.5" /> {teamGroups.length} groups · {teamJobs.length} jobs
            </span>
          </div>
          {teamGroups.length === 0 && (
            <div data-testid="field-team-empty" className="bg-[#141414] border border-[#262626] rounded-md p-6 text-center">
              <p className="text-[#A3A3A3] text-sm">Nothing scheduled {isToday ? 'today' : 'on this day'}.</p>
            </div>
          )}
          {teamGroups.map((g) => (
            <section key={g.id} data-testid={`field-team-group-${g.id}`} className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <span style={{ background: g.color }} className="w-2.5 h-2.5 rounded-full shrink-0" />
                <h4 className="text-white text-sm font-bold">{g.name}</h4>
                <span className="text-[#A3A3A3] text-xs">· {g.jobs.length} job{g.jobs.length === 1 ? '' : 's'}</span>
              </div>
              <div className="space-y-2">
                {g.jobs.map((j) => (
                  <JobCard key={j.id} job={j} kind={g.id === 'unassigned' ? 'claim' : 'team'} currentUser={user} onOpen={openPreview} onClaim={handleClaimJob} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* ─── Single shared preview sheet ─── */}
      <JobPreviewSheet
        open={!!selected && !showWizard}
        onOpenChange={(o) => { if (!o) setSelected(null); }}
        job={selected}
        origin={origin}
        user={user}
        latestCompletion={latestCompletion}
        onClaimJob={handleClaimJob}
        onStatusChange={handleStatusChange}
        onClaimShift={() => setClaimOpen(true)}
        onStartJob={handleStartJob}
        onCompleteJob={openWizard}
        onCreateEstimate={openEstimateWizard}
      />

      {showWizard && selected && (
        <CompletionWizard
          appointment={selected}
          initialOutcome={wizardOutcome}
          onClose={(submitted) => {
            setShowWizard(false);
            setWizardOutcome('');
            if (submitted) {
              setSelected(null);
              refreshAll();
            }
          }}
        />
      )}
      <ClaimShiftDialog
        open={claimOpen}
        appointment={selected}
        technicianName={user?.name}
        onClose={() => setClaimOpen(false)}
        onClaimed={(data) => {
          if (selected) setSelected({ ...selected, status: 'en_route', arrival_eta_minutes: data?.arrival_eta_minutes });
          refreshAll();
        }}
      />
    </div>
  );
};

export default EmployeeSchedule;
